import type { IconName } from '@/lib/icones-police';
import { ActionBar, Button, Dialog } from '@pms-digital/design-system';

interface DialogueConfirmationProps {
  open: boolean;
  title: string;
  /** One line under the title. */
  description: string;
  /** The icon of the round, for what is not destructive (a destructive dialog always shows `warning`). */
  icon?: IconName;
  /** Label of the button that closes without acting. */
  cancelLabel: string;
  /** Label of the button that goes ahead. */
  confirmLabel: string;
  /** The action cannot be taken back (an interruption, #7): the dialog and its button are destructive. */
  confirmDanger?: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

/**
 * The confirmation of an action: the `Dialog` of the PMS design system in its centred tone
 * (informational, or destructive for what cannot be taken back) with its `ActionBar`, "annuler"
 * first, then the action. A composition with Rosetta's words (`open`, `onCancel`, `onConfirm`),
 * three screens ask for it; it adds no look. Escape and a click outside cancel; the focus starts
 * on "annuler", so that a stray Enter never confirms, and goes back to what had it when it closes (the package does it).
 */
export function DialogueConfirmation({
  open,
  title,
  description,
  icon,
  cancelLabel,
  confirmLabel,
  confirmDanger = false,
  onCancel,
  onConfirm,
}: DialogueConfirmationProps) {
  return (
    <Dialog
      open={open}
      onOpenChange={(ouvert) => {
        if (!ouvert) onCancel();
      }}
      tone={confirmDanger ? 'destructive' : 'informational'}
      title={title}
      description={description}
      {...(icon === undefined || confirmDanger ? {} : { icon })}
      actions={
        <ActionBar
          align="center"
          secondary={
            <Button variant="ghost" onClick={onCancel}>
              {cancelLabel}
            </Button>
          }
          primary={
            <Button variant={confirmDanger ? 'destructive' : 'secondary'} onClick={onConfirm}>
              {confirmLabel}
            </Button>
          }
        />
      }
    />
  );
}
